import { SearchInput } from "@amzn/storm-ui";
import { isEmpty } from "lodash";
import { type ChangeEvent, type ReactElement, useRef } from "react";
import { useStore } from "zustand";

import { SearchSortAttribute } from "../../../api/adsApi/adsApi.interfaces";
import useEncodedFilterStateUrl from "../../../hooks/misc/useEncodedFilterStateUrl";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import { unifiedFilterStore } from "../../../store/unifiedFilters";
import { IHubContentProvider } from "../../../types/providers";

export const SEARCH_BAR_ID = "search-input";

export const SearchBar = (): ReactElement => {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [setEncodedFilterStateUrl] = useEncodedFilterStateUrl();

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [unifiedSearchQuery, setUnifiedSearchQuery, setIsMetadataDirty] =
        useStore(unifiedFilterStore, (state) => [
            state.searchQuery,
            state.setSearchQuery,
            state.setIsMetadataDirty,
        ]);

    const iHubContentProvider = useStore(discoveryFilterStore, (state) =>
        state.getContentSubTypeProvider(),
    );
    const [
        searchQuery,
        setSearchQuery,
        resetSearchQueryAndSortAttribute,
        setSortAttribute,
    ] = useStore(discoveryFilterStore, (state) => [
        state.getFilters()[iHubContentProvider]?.searchQuery,
        state.setSearchQuery,
        state.resetSearchQueryAndSortAttribute,
        state.setSortAttribute,
    ]);

    const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        const value = target.value ?? "";

        if (useUnifiedSearch) {
            setUnifiedSearchQuery(value);
        } else {
            setSearchQuery(value);
            if (
                iHubContentProvider === IHubContentProvider.MARKETING_NARRATIVES
            ) {
                const sortAttribute = isEmpty(value.trim())
                    ? SearchSortAttribute.LAST_MODIFIED_DATE
                    : SearchSortAttribute.RELEVANCY;
                setSortAttribute(sortAttribute);
            }

            setEncodedFilterStateUrl();
        }
    };

    const handleClear = () => {
        if (useUnifiedSearch) {
            setUnifiedSearchQuery("");
            setIsMetadataDirty(true);
        } else {
            // Handle original filter state
            resetSearchQueryAndSortAttribute();
            setEncodedFilterStateUrl();
        }
    };

    return useUnifiedSearch ? (
        <>
            <SearchInput
                id={SEARCH_BAR_ID}
                data-testid="search-input"
                inputRef={searchInputRef}
                value={unifiedSearchQuery}
                onChange={handleChange}
                onClear={handleClear}
                placeholder="Search"
                fullWidth={true}
            />
        </>
    ) : (
        <SearchInput
            id={SEARCH_BAR_ID}
            data-testid="search-input"
            placeholder="Search"
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={handleChange}
            onClear={handleClear}
        />
    );
};

export default SearchBar;
